// The bundle's world_rotation: the orientation of the TRAINING frame, undone at render time.
//
// A bundle is trained in whatever frame its capture rig used, and the decoders emit
// Gaussians in that frame — myra_v5's is 180° about Z from the scene's, so the lift is
// correct and the avatar still renders upside down. scene.json states the offending
// orientation as `world_rotation` (XYZ Euler degrees) and it is undone in the SAME two
// places world_offset is: the SplatMesh's own transform stands the splats up, and the
// plücker camera travels the INVERSE so appr is still conditioned on a camera in the frame
// it was trained in.
//
// Both halves or neither. Rotate only the mesh and the geometry stands up while the
// view-dependent shading quietly reads from a viewpoint 180° from the real one — no error,
// just wrong colour, which is the failure mode render/localFrameCamera.ts exists to
// document. Rotate only the camera and nothing moves at all.
//
// Rotating the MESH rather than each Gaussian is deliberate: it is exact for a rigid
// rotation, costs nothing per splat, and leaves every lift output in the training frame —
// where the branch's `neutralVertices`, the driver-mesh part fits (inference/partFrameFit.ts)
// and the `__OGS_BRANCHES__` debug seam all still compare against the trained mesh.

import * as THREE from "three";

const DEGREES_TO_RADIANS = Math.PI / 180;

/** scene.json `world_rotation` → a quaternion, or null when there is nothing to undo.
 *  Null rather than an identity quaternion so an unrotated bundle takes byte-identical
 *  paths to the ones it took before this existed. */
export function worldRotationQuaternion(degrees: readonly number[] | undefined | null): THREE.Quaternion | null {
  if (!degrees || degrees.length !== 3) return null;
  const [x, y, z] = degrees;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  if (x === 0 && y === 0 && z === 0) return null;
  const euler = new THREE.Euler(x * DEGREES_TO_RADIANS, y * DEGREES_TO_RADIANS, z * DEGREES_TO_RADIANS, "XYZ");
  return new THREE.Quaternion().setFromEuler(euler);
}

/** The bundle's declared rotation, overridden by `?worldrot=x,y,z` (degrees).
 *  The A/B knob for eyeballing an upside-down bundle before its scene.json says so —
 *  the sibling of ?moment=, and like it the URL always wins. `?worldrot=0,0,0` is a
 *  valid override meaning "render in the trained frame". */
export function resolveWorldRotation(
  declared: readonly number[] | undefined | null,
  search = window.location.search,
): [number, number, number] {
  const fallback: [number, number, number] = [
    Number(declared?.[0]) || 0,
    Number(declared?.[1]) || 0,
    Number(declared?.[2]) || 0,
  ];
  const raw = new URLSearchParams(search).get("worldrot");
  if (raw === null) return fallback;
  const parts = raw.split(",").map((part) => parseFloat(part));
  if (parts.length !== 3 || !parts.every((part) => Number.isFinite(part))) {
    console.warn(`[ogs] ?worldrot=${raw} is not three numbers — using the bundle's [${fallback.join(', ')}]`);
    return fallback;
  }
  console.info(`[ogs] worldRotation=[${parts.join(', ')}] (?worldrot= override)`);
  return [parts[0], parts[1], parts[2]];
}
